import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { ArrowLeft, Loader2 } from 'lucide-react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { coachingNotesService, CoachablePerson } from '@/services/coachingNotesService'
import { useManageCoachingNote } from '@/hooks/useManageCoachingNote'

interface Props {
  open: boolean
  onClose: () => void
  /** Skip the picker and open on this person (must be in the coachable list). */
  personId?: string | null
}

const MAX_NOTE = 2000

/** "Jul 7" — real timestamps (createdAt), never picked calendar dates. */
function formatMonthDay(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

// The universal coaching door: pick anyone below your level, read their
// conversation history, log a new one. The picker is server-filtered
// (GET /coachable), so it can never offer someone the notes routes would 403.
export default function CoachDialog({ open, onClose, personId = null }: Props) {
  const queryClient = useQueryClient()
  const [person, setPerson] = useState<CoachablePerson | null>(null)
  const [personQuery, setPersonQuery] = useState('')
  const [draft, setDraft] = useState('')
  // Per-note manage state: which note is being edited (and its draft), which
  // note has the delete confirmation open.
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editDraft, setEditDraft] = useState('')
  const [deleteId, setDeleteId] = useState<string | null>(null)

  // Reopening starts fresh at the picker — the last session's person and
  // half-typed note must not leak into a new conversation.
  useEffect(() => {
    if (!open) return
    setPerson(null)
    setPersonQuery('')
    setDraft('')
    setEditingId(null)
    setEditDraft('')
    setDeleteId(null)
  }, [open])

  const { data: people = [], isLoading: peopleLoading, isError: peopleError } = useQuery({
    queryKey: ['coachable-people'],
    queryFn: () => coachingNotesService.coachable(),
    staleTime: 5 * 60 * 1000,
    enabled: open,
  })

  // A requested person lands you on their log once the picker data is in;
  // someone not on the list (not coachable) just leaves you at the picker.
  useEffect(() => {
    if (!open || !personId || person) return
    const match = people.find(p => p.id === personId)
    if (match) setPerson(match)
  }, [open, personId, people, person])

  // Typing filters; it never picks for you — same as the send-task picker.
  const matches = useMemo(() => {
    const q = personQuery.trim().toLowerCase()
    if (!q) return people
    return people.filter(p =>
      p.name.toLowerCase().includes(q) || (p.position || '').toLowerCase().includes(q)
    )
  }, [people, personQuery])

  // SAME key as the talent Development panel so the caches share.
  const { data: notesResult, isLoading: notesLoading } = useQuery({
    queryKey: ['coaching-conversations', person?.id],
    queryFn: () => coachingNotesService.list(person!.id),
    enabled: open && !!person,
  })
  // Shouldn't happen — the picker is pre-filtered — but the service maps the
  // level-gate 403 to canCoach:false, so honor it: no notes, no compose.
  const forbidden = notesResult?.canCoach === false
  const notes = !forbidden ? notesResult?.notes : undefined

  const save = useMutation({
    mutationFn: (text: string) => coachingNotesService.create(person!.id, text),
    onSuccess: () => {
      setDraft('')
      queryClient.invalidateQueries({ queryKey: ['coaching-conversations', person?.id] })
      // The Talent Coaching page + Development pulse list this note too.
      queryClient.invalidateQueries({ queryKey: ['talent-coaching'] })
      queryClient.invalidateQueries({ queryKey: ['talent-pipeline'] })
      toast.success('Conversation logged')
      // Deliberately stays on the person so you can read what you just wrote.
    },
    onError: () => {
      toast.error('Could not save the note')
    },
  })

  const canSave = !!person && draft.trim().length > 0 && !save.isPending

  // Edit/delete share mutations (and toasts/invalidation) with the profile
  // Coaching log section via useManageCoachingNote.
  const { update, remove } = useManageCoachingNote(person?.id)
  const canSaveEdit = editDraft.trim().length > 0 && !update.isPending
  const saveEdit = (noteId: string) =>
    update.mutate({ noteId, text: editDraft.trim() }, { onSuccess: () => setEditingId(null) })
  const confirmDelete = () => {
    if (deleteId) remove.mutate(deleteId)
    setDeleteId(null)
  }

  return (
    <Dialog open={open} onOpenChange={next => !next && onClose()}>
      <DialogContent className="pointer-events-auto sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle className="text-base">
            {person ? (
              <span className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => { setPerson(null); setDraft(''); setEditingId(null); setDeleteId(null) }}
                  className="rounded-full p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                  aria-label="Back to people"
                  title="Back to people"
                >
                  <ArrowLeft className="h-4 w-4" />
                </button>
                <span className="truncate">
                  <span className="font-semibold text-slate-900">{person.name}</span>
                  {person.position && <span className="font-normal text-slate-400"> — {person.position}</span>}
                </span>
              </span>
            ) : (
              'Coach someone'
            )}
          </DialogTitle>
        </DialogHeader>

        {!person ? (
          <div className="space-y-1.5">
            <Input
              value={personQuery}
              onChange={e => setPersonQuery(e.target.value)}
              placeholder={peopleLoading ? 'Loading people…' : 'Start typing a name'}
              autoComplete="off"
              aria-label="Who are you coaching?"
            />
            <div
              role="listbox"
              aria-label="Coachable people"
              className="max-h-64 overflow-y-auto rounded-md border border-slate-200"
            >
              {matches.length === 0 ? (
                <p className="px-3 py-2 text-sm text-slate-400">
                  {peopleLoading
                    ? 'Loading people…'
                    : peopleError
                      ? 'Couldn’t load team members — try again in a moment.'
                      : people.length === 0
                        ? 'No team members below your level yet.'
                        : `Nobody matches “${personQuery.trim()}”`}
                </p>
              ) : (
                matches.map(p => (
                  <button
                    key={p.id}
                    type="button"
                    role="option"
                    aria-selected={false}
                    onClick={() => setPerson(p)}
                    className="flex w-full items-baseline gap-1.5 px-3 py-2 text-left text-sm hover:bg-slate-50"
                  >
                    <span className="truncate text-slate-900">{p.name}</span>
                    {p.position && (
                      <span className="truncate text-xs text-slate-400">{p.position}</span>
                    )}
                  </button>
                ))
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="max-h-64 space-y-2 overflow-y-auto">
              {!notesLoading && notesResult?.scope === 'own' && (
                <p className="px-1 text-xs text-slate-400">Only the notes you logged show here.</p>
              )}
              {notesLoading ? (
                <p className="flex items-center gap-2 px-1 py-2 text-sm text-slate-400">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading conversations…
                </p>
              ) : forbidden ? (
                <p className="px-1 py-2 text-sm text-slate-400">
                  You can only view coaching notes for team members below your level.
                </p>
              ) : !notes || notes.length === 0 ? (
                <p className="px-1 py-2 text-sm text-slate-400">No conversations logged yet.</p>
              ) : (
                notes.map(note => (
                  <div key={note.id} className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
                    <p className="text-xs font-medium text-slate-400">
                      {note.author.name} · {formatMonthDay(note.createdAt)}
                      {note.editedAt && <span className="text-slate-300"> · edited</span>}
                    </p>
                    {editingId === note.id ? (
                      <div className="mt-1.5 space-y-1.5">
                        <Textarea
                          value={editDraft}
                          onChange={e => setEditDraft(e.target.value)}
                          maxLength={MAX_NOTE}
                          rows={3}
                          aria-label="Edit note"
                        />
                        <div className="flex justify-end gap-2">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => { setEditingId(null); setEditDraft('') }}
                          >
                            Cancel
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            disabled={!canSaveEdit}
                            onClick={() => saveEdit(note.id)}
                            className="bg-[#E51636] text-white hover:bg-[#DD0031]"
                          >
                            {update.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            Save note
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <p className="mt-0.5 whitespace-pre-wrap text-sm leading-relaxed text-slate-900">{note.text}</p>
                        {note.canManage && (
                          <div className="mt-1 flex gap-3">
                            <button
                              type="button"
                              className="text-xs font-medium text-slate-400 hover:text-slate-600"
                              onClick={() => { setEditingId(note.id); setEditDraft(note.text) }}
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              className="text-xs font-medium text-slate-400 hover:text-[#E51636]"
                              onClick={() => setDeleteId(note.id)}
                            >
                              Delete
                            </button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                ))
              )}
            </div>

            {!forbidden && (
              <div className="space-y-2">
                <Textarea
                  value={draft}
                  onChange={e => setDraft(e.target.value)}
                  maxLength={MAX_NOTE}
                  rows={3}
                  placeholder="Log a conversation…"
                  aria-label="Log a conversation"
                />
                <div className="flex justify-end">
                  <Button
                    type="button"
                    disabled={!canSave}
                    onClick={() => save.mutate(draft.trim())}
                    className="bg-[#E51636] text-white hover:bg-[#DD0031]"
                  >
                    {save.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Save
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        <p className="text-xs text-slate-400">
          Visible only to trainers and leaders above their level — never to the team member.
        </p>

        <AlertDialog open={!!deleteId} onOpenChange={next => !next && setDeleteId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete this note?</AlertDialogTitle>
              <AlertDialogDescription>This can&rsquo;t be undone.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={confirmDelete}
                className="bg-[#E51636] text-white hover:bg-[#DD0031]"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DialogContent>
    </Dialog>
  )
}
